import {
  MarkNamespace,
  MARK_SHAPES,
  type MarkShape,
} from "@bennara/shared";
import * as THREE from "three";
import { CONFIG } from "../../config";

const TEXTURE_LONG_SIDE = 256;

const textures = new Map<MarkNamespace.Type, THREE.CanvasTexture>();
const geometries = new Map<MarkNamespace.Type, THREE.PlaneGeometry>();

/** Lift along the surface normal so the stamp does not flicker against the skin. */
export const SHAPE_SURFACE_OFFSET = 0.012;

function rasterize(shape: MarkShape): THREE.CanvasTexture {
  const [vx, vy, vw, vh] = shape.viewBox;
  const long = Math.max(vw, vh);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round((TEXTURE_LONG_SIDE * vw) / long));
  canvas.height = Math.max(1, Math.round((TEXTURE_LONG_SIDE * vh) / long));

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not rasterize mark shape");
  }

  const scale = canvas.width / vw;
  ctx.setTransform(scale, 0, 0, scale, -vx * scale, -vy * scale);
  ctx.fillStyle = "#ffffff";
  ctx.fill(new Path2D(shape.d), "evenodd");

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}

export function getMarkShapeTexture(
  type: MarkNamespace.Type,
): THREE.CanvasTexture {
  let texture = textures.get(type);
  if (!texture) {
    texture = rasterize(MARK_SHAPES[type]);
    textures.set(type, texture);
  }
  return texture;
}

/** Local plane whose long side matches the sphere diameter, before zoom scale. */
export function getMarkShapeGeometry(
  type: MarkNamespace.Type,
): THREE.PlaneGeometry {
  let geometry = geometries.get(type);
  if (!geometry) {
    const [, , vw, vh] = MARK_SHAPES[type].viewBox;
    const long = Math.max(vw, vh);
    const size = CONFIG.marker.radius * 2;
    geometry = new THREE.PlaneGeometry((size * vw) / long, (size * vh) / long);
    geometry.translate(0, 0, SHAPE_SURFACE_OFFSET);
    geometries.set(type, geometry);
  }
  return geometry;
}

export function createShapeMarkerMaterial(
  color: string,
  type: MarkNamespace.Type,
): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    map: getMarkShapeTexture(type),
    color: new THREE.Color(color),
    alphaTest: 0.5,
    side: THREE.DoubleSide,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
  });
}

export function createShapeMarker(
  material: THREE.Material,
  type: MarkNamespace.Type,
): THREE.Mesh {
  const mesh = new THREE.Mesh(getMarkShapeGeometry(type), material);
  mesh.renderOrder = CONFIG.marker.renderOrder;
  return mesh;
}

const _zAxis = new THREE.Vector3(0, 0, 1);
const _worldUp = new THREE.Vector3(0, 1, 0);
const _projectedUp = new THREE.Vector3();
const _localUp = new THREE.Vector3();
const _align = new THREE.Quaternion();
const _roll = new THREE.Quaternion();

/** Point the object's local +Z along `normal`, with local +Y following world up. */
export function orientToSurface(
  object: THREE.Object3D,
  normal: THREE.Vector3,
): void {
  const n = _projectedUp.copy(normal).normalize();
  _align.setFromUnitVectors(_zAxis, n);

  _localUp.copy(_worldUp).addScaledVector(n, -_worldUp.dot(n));
  if (_localUp.lengthSq() < 1e-8) {
    _localUp.set(1, 0, 0);
  } else {
    _localUp.normalize();
  }

  _zAxis.set(0, 1, 0).applyQuaternion(_align);
  const twist = Math.atan2(
    _zAxis.clone().cross(_localUp).dot(n),
    _zAxis.dot(_localUp),
  );
  _roll.setFromAxisAngle(n, twist);
  object.quaternion.copy(_align).premultiply(_roll);
  _zAxis.set(0, 0, 1);
}

const _raycaster = new THREE.Raycaster();
const _pos = new THREE.Vector3();
const _outward = new THREE.Vector3();
const _origin = new THREE.Vector3();
const _dir = new THREE.Vector3();

/**
 * Normal of the body face nearest `point`.
 * Casts a short ray from just outside the point back into the mesh.
 * Falls back to the direction from the origin through the point.
 */
export function surfaceNormalAt(
  body: THREE.Object3D,
  point: { x: number; y: number; z: number },
  target = new THREE.Vector3(),
): THREE.Vector3 {
  _pos.set(point.x, point.y, point.z);
  _outward.copy(_pos);
  if (_outward.lengthSq() < 1e-8) _outward.set(0, 1, 0);
  else _outward.normalize();

  body.updateWorldMatrix(true, true);
  _origin.copy(_pos).addScaledVector(_outward, 0.2);
  _dir.copy(_outward).negate();
  _raycaster.set(_origin, _dir);
  _raycaster.far = 0.45;
  const hits = _raycaster.intersectObject(body, true);
  _raycaster.far = Infinity;

  let best: THREE.Intersection | null = null;
  let bestDist = Infinity;
  for (const hit of hits) {
    if (!hit.face) continue;
    const dist = hit.point.distanceToSquared(_pos);
    if (dist < bestDist) {
      best = hit;
      bestDist = dist;
    }
  }

  if (best?.face) {
    target
      .copy(best.face.normal)
      .transformDirection(best.object.matrixWorld)
      .normalize();
    if (target.dot(_outward) < 0) target.negate();
    return target;
  }

  return target.copy(_outward);
}
